import { useState, useCallback, useEffect } from 'react';
import { showToast } from '../components/atoms/Toast/Toast';
import authService from '../services/authService';
import { MS_CONFIGS } from '../components/organisms/SecufireActionLogs/SecufireActionLogs';
 
const SECUFIRE_MICROSERVICES = [
  'historic-fire_service', 'active-fire_service', 'physical_model',
  'dfci_service', 'vegetation_service', 'weather_service',
];
 
const formatDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
 
export const useSecufireLogs = ({ isAuthenticated, accessToken, activeSection, fetchLiveLogs }) => {
 
  const [secufireLogsData,          setSecufireLogsData]          = useState(null);
  const [loadingSecufireLogs,       setLoadingSecufireLogs]       = useState(false);
  const [secufireStartDate,         setSecufireStartDate]         = useState(null);
  const [secufireEndDate,           setSecufireEndDate]           = useState(null);
  const [secufireSelectedAdminId,   setSecufireSelectedAdminId]   = useState(null);
  const [secufireSelectedUserId,    setSecufireSelectedUserId]    = useState(null);
  const [secufireUserDetailData,    setSecufireUserDetailData]    = useState(null);
  const [loadingSecufireUserDetail, setLoadingSecufireUserDetail] = useState(false);
  const [selectedMs, setSelectedMs] = useState(
  new Set(SECUFIRE_MICROSERVICES)
);
  const [secufireUsers,             setSecufireUsers]             = useState([]);
  const [endpointDrillData,         setEndpointDrillData]         = useState(null);
  const [userDrillData,             setUserDrillData]             = useState(null);
  const [refreshingSecufire,        setRefreshingSecufire]        = useState(false);
  const [isInternal,                setIsInternal]                = useState(null);
 
  const fetchSecufireLogs = useCallback(async (start, end, adminId) => {
    if (!isAuthenticated || !accessToken) return;
    setLoadingSecufireLogs(true);
    try {
      const results = await Promise.all(
        SECUFIRE_MICROSERVICES.map((ms) =>
          authService
            .getGlobalOverview(
              ms,
              formatDate(start ?? secufireStartDate),
              formatDate(end   ?? secufireEndDate),
              adminId ?? secufireSelectedAdminId,
              isInternal
            )
            .then((data) => ({
              microservice: ms,
              total_usage:  data.summary?.total_activities || 0,
              endpoints: (data.by_functionality || []).map((f) => ({
              endpoint: f.functionality || f.endpoint || '',
              usage:    f.total_usage || 0,
})),
              daily_trend: data.daily_trend || [],
              all_users:   data.all_users   || [],
            }))
            .catch(() => ({ microservice: ms, total_usage: 0, endpoints: [], daily_trend: [], all_users: [] }))
        )
      );
      setSecufireLogsData({ microservices: results });
 
      const userMap = {};
      results.forEach(({ all_users }) => {
        all_users.forEach((u) => {
          if (!userMap[u.user_id]) {
            userMap[u.user_id] = { user_id: u.user_id, username: u.username, active: u.active, activity_count: 0 };
          }
          userMap[u.user_id].activity_count += u.activity_count || 0;
        });
      });
      setSecufireUsers(
        Object.values(userMap).sort((a, b) => b.activity_count - a.activity_count)
      );
    } catch (e) {
      showToast(e.message || 'Erreur chargement SecuFire', 'error');
    } finally {
      setLoadingSecufireLogs(false);
    }
  }, [isAuthenticated, accessToken, secufireStartDate, secufireEndDate, secufireSelectedAdminId, isInternal]);
 
  const fetchSecufireUserDetail = useCallback(async (userId, msSet) => {
    if (!userId) { setSecufireUserDetailData(null); return; }
    setLoadingSecufireUserDetail(true);
    try {
      const msKeys = msSet instanceof Set ? [...msSet]
        : Array.isArray(msSet) ? msSet
        : [msSet || SECUFIRE_MICROSERVICES[0]];
 
      if (msKeys.length === 1) {
        const data = await authService.getUserDetailed(
          userId, msKeys[0],
          formatDate(secufireStartDate), formatDate(secufireEndDate),
          secufireSelectedAdminId,
          isInternal
        );
        setSecufireUserDetailData(data);
      } else {
        const responses = await Promise.all(
          msKeys.map((ms) =>
            authService.getUserDetailed(
              userId, ms,
              formatDate(secufireStartDate), formatDate(secufireEndDate),
              secufireSelectedAdminId,
              isInternal
            ).catch(() => null)
          )
        );
        const valid = responses.filter(Boolean);
        if (!valid.length) { setSecufireUserDetailData(null); return; }
        setSecufireUserDetailData({
          user_id:           valid[0].user_id,
          username:          valid[0].username,
          active:            valid[0].active,
          total_activities:  valid.reduce((s, d) => s + (d.total_activities || 0), 0),
          by_functionality:  valid.flatMap((d) => d.by_functionality  || []),
          by_microservice:   valid.flatMap((d) => d.by_microservice   || []),
          recent_activities: valid
            .flatMap((d) => d.recent_activities || [])
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .slice(0, 50),
        });
      }
    } catch (e) {
      showToast(e.message || 'Erreur détails SecuFire', 'error');
      setSecufireUserDetailData(null);
    } finally {
      setLoadingSecufireUserDetail(false);
    }
  }, [secufireStartDate, secufireEndDate, secufireSelectedAdminId, isInternal]);
 
  useEffect(() => {
    if (activeSection !== 'secufireActionLogs') return;
    fetchSecufireLogs(secufireStartDate, secufireEndDate, secufireSelectedAdminId);
    fetchLiveLogs([...selectedMs][0] || null, secufireSelectedAdminId);
  }, [activeSection, secufireStartDate, secufireEndDate, secufireSelectedAdminId, isInternal]); // eslint-disable-line
 
  useEffect(() => {
    if (activeSection === 'secufireActionLogs') {
      fetchSecufireUserDetail(secufireSelectedUserId, selectedMs);
    }
  }, [secufireSelectedUserId, selectedMs, activeSection]); // eslint-disable-line
 
  useEffect(() => {
    if (userDrillData?.user_id) {
      handleUserDrill({ user_id: userDrillData.user_id, username: userDrillData.username });
    }
  }, [selectedMs]); // eslint-disable-line
 
  const handleSecufireDateChange = (start, end) => {
    setSecufireStartDate(start);
    setSecufireEndDate(end);
  };
 
  const handleMsSelect = (msKeyOrSet) =>
    setSelectedMs(msKeyOrSet instanceof Set ? msKeyOrSet : new Set([msKeyOrSet]));
 
  const handleSecufireAdminSelect = useCallback((adminId) => {
    setSecufireSelectedAdminId(adminId ?? null);
    setEndpointDrillData(null);
    setUserDrillData(null);
  }, []);
 
  const handleIsInternalChange = useCallback((val) => {
    setIsInternal(val);
    setEndpointDrillData(null);
    setUserDrillData(null);
    setSecufireUserDetailData(null);
    setSecufireSelectedUserId(null);
  }, []);
 
const handleEndpointDrill = useCallback(async ({ endpoint, fullEndpoint, microservice, color, msLabel }) => {
    setEndpointDrillData({ loading: true, endpoint, fullEndpoint, color, msLabel, calls: [] });
    try {
      
      const data = await authService.getEndpointHistory(
        microservice, fullEndpoint,
        formatDate(secufireStartDate), formatDate(secufireEndDate),
        secufireSelectedAdminId,
        isInternal
      );

      // Matching strict, du plus précis au moins précis.
      // On NE matche PLUS sur le dernier segment seul (endsWith / split.pop),
      // trop ambigu quand plusieurs endpoints partagent un suffixe (.list, .get, etc.)
      const candidates = data.endpoints || [];

      let entry =
        candidates.find((e) => e.endpoint === fullEndpoint) ||
        candidates.find((e) => e.endpoint === `${microservice}.${endpoint}`) ||
        candidates.find((e) => e.endpoint === endpoint);

      if (!entry && candidates.length) {
        // Debug: aide à diagnostiquer un vrai mismatch de format API
        console.warn(
          '[handleEndpointDrill] Aucun match exact pour',
          { fullEndpoint, endpoint, microservice },
          'candidats reçus:',
          candidates.map((e) => e.endpoint)
        );
      }

      const calls = (entry?.calls || [])
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .map(({ timestamp, username, details }) => ({ timestamp, username: username || null, details: details || null }));
      setEndpointDrillData({ loading: false, endpoint, fullEndpoint, color, msLabel, calls });
    } catch (e) {
      setEndpointDrillData((prev) => prev ? { ...prev, loading: false, calls: [], error: e?.message } : null);
      showToast('Erreur détail endpoint', 'error');
    }
  }, [secufireStartDate, secufireEndDate, secufireSelectedAdminId, isInternal]);
 
  const handleUserDrill = useCallback(async ({ user_id, username }) => {
    setUserDrillData({ loading: true, user_id, username, color: '#be1622', calls: [] });
    try {
      const msKeys = selectedMs instanceof Set ? [...selectedMs]
        : Array.isArray(selectedMs) ? selectedMs : [selectedMs];
 
      const responses = await Promise.allSettled(
        msKeys.map((ms) =>
          authService.getUserDetailed(
            user_id, ms,
            formatDate(secufireStartDate), formatDate(secufireEndDate),
            secufireSelectedAdminId,
            isInternal
          ).catch(() => null)
        )
      );
 
      const allCalls = [];
      responses
        .filter((r) => r.status === 'fulfilled' && r.value)
        .forEach((r, i) => {
          const ms  = msKeys[i];
          const val = r.value;
          const resolvedLabel = MS_CONFIGS[ms]?.label || ms;
          const resolvedColor = MS_CONFIGS[ms]?.color || '#64748b';
 
          if (val.recent_activities?.length) {
            val.recent_activities.forEach((a) => {
              const raw = a.service || a.endpoint || a.functionality || '';
              const ep  = raw.includes('.') ? raw.split('.').slice(1).join('.') : raw;
              allCalls.push({ timestamp: a.timestamp, microservice: ms, msLabel: resolvedLabel, msColor: resolvedColor, endpoint: ep || a.details || null });
            });
          } else {
            (val.by_functionality || []).forEach((f) => {
              const ts = f.last_call || f.last_activity || null;
              if (!ts) return;
              allCalls.push({
                timestamp: ts,
                microservice: f.microservice || ms,
                msLabel: MS_CONFIGS[f.microservice || ms]?.label || resolvedLabel,
                msColor: MS_CONFIGS[f.microservice || ms]?.color || resolvedColor,
                endpoint: f.endpoint || f.functionality?.split('.').slice(1).join('.') || null,
              });
            });
          }
        });
 
      setUserDrillData({
        loading: false, user_id, username, color: '#be1622',
        calls: allCalls.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
      });
    } catch (e) {
      showToast("Erreur historique utilisateur", 'error');
      setUserDrillData(null);
    }
  }, [selectedMs, secufireStartDate, secufireEndDate, secufireSelectedAdminId, isInternal]);
 
  const handleRefresh = useCallback(async () => {
    if (refreshingSecufire) return;
    setRefreshingSecufire(true);
    try {
      await fetchSecufireLogs(secufireStartDate, secufireEndDate, secufireSelectedAdminId);
      await fetchLiveLogs([...selectedMs][0] || null, secufireSelectedAdminId);
      if (secufireSelectedUserId) await fetchSecufireUserDetail(secufireSelectedUserId, selectedMs);
    } catch {
      showToast('Erreur rafraîchissement', 'error');
    } finally {
      setRefreshingSecufire(false);
    }
  }, [refreshingSecufire, fetchSecufireLogs, secufireStartDate, secufireEndDate,
      secufireSelectedAdminId, fetchLiveLogs, selectedMs,
      secufireSelectedUserId, fetchSecufireUserDetail]);
 
  const patchActivityCount = useCallback((data) => {
    setSecufireUsers((prev) => prev.map((u) =>
      String(u.user_id) === String(data.user_id)
        ? { ...u, activity_count: (u.activity_count || 0) + 1 }
        : u
    ));
  }, []);
 
  const patchUserDetail = useCallback((data) => {
    setSecufireUserDetailData((prev) =>
      prev && String(prev.user_id) === String(data.user_id)
        ? { ...prev, total_activities: data.alltime_total }
        : prev
    );
  }, []);
 
  const reset = useCallback(() => {
    setSecufireLogsData(null);
    setSecufireStartDate(null);
    setSecufireEndDate(null);
    setSecufireSelectedUserId(null);
    setSecufireUserDetailData(null);
    setSecufireSelectedAdminId(null);
    setSecufireUsers([]);
    setSelectedMs(new Set(SECUFIRE_MICROSERVICES));
    setEndpointDrillData(null);
    setUserDrillData(null);
    setIsInternal(null);
  }, []);
 
  return {
    patchActivityCount,
    patchUserDetail,
    reset,
    props: {
      secufireLogsData,
      loadingSecufireLogs,
      secufireStartDate,
      secufireEndDate,
      onSecufireDateChange:     handleSecufireDateChange,
      secufireUsers,
      secufireSelectedUserId,
      onSecufireUserSelect:     (id) => setSecufireSelectedUserId(id),
      secufireUserDetailData,
      loadingSecufireUserDetail,
      selectedMs,
      onMsSelect:               handleMsSelect,
      selectedAdminId:          secufireSelectedAdminId,
      onAdminSelect:            handleSecufireAdminSelect,
      onRefreshSecufireLogs:    handleRefresh,
      refreshingSecufire,
      onEndpointDrill:          handleEndpointDrill,
      endpointDrillData,
      onEndpointDrillClose:     () => setEndpointDrillData(null),
      onUserDrill:              handleUserDrill,
      userDrillData,
      onUserDrillClose:         () => setUserDrillData(null),
      isInternal,
      onIsInternalChange:       handleIsInternalChange,
    },
  };
};